/*
  RootWrapper.jsx
*/

// Npm.
import React from 'react';
import { keyframes } from '@emotion/css';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Animations.
const fadeIn = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  }
});

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    color: theme.color_text_primary,
    backgroundColor: theme.color_background,
    transition: 'background-color 0.2s ease',
    fontWeight: '400',
    fontSize: '1rem',
    lineHeight: '1.5',
    boxSizing: 'border-box',
    '& *, *::before, *::after': {
      boxSizing: 'inherit',
    },
    '& strong, b': {
      fontWeight: '700',
    },
    '& .fadeIn': {
      animation: `${fadeIn} 0.2s ease-out`,
    },
    '& h1, h2, h3, h4, h5, h6, p': {
      margin: 0,
    },
    '& h1, .h1': {
      fontSize: '2rem',
      lineHeight: '1.167',
      fontWeight: '400',
    },
    '& h2, .h2': {
      fontSize: '1.5625rem',
      lineHeight: '1.235',
      fontWeight: '600',
    },
    '& h3, .h3': {
      fontSize: '1.25rem',
      lineHeight: '1.334',
      fontWeight: '600',
    },
    '& h4, .h4': {
      fontSize: '1.15rem',
      lineHeight: '1.6',
      fontWeight: '600',
    },
    '& .body1': {
      fontSize: '1rem',
      lineHeight: '1.5',
      fontWeight: '400',
    },
    '& .body2': {
      fontSize: '0.875rem',
      lineHeight: '1.43',
      fontWeight: '400',
    },
    '& .overline': {
      fontSize: '0.75rem',
      lineHeight: '1.2',
      fontWeight: '600',
      letterSpacing: '0.05em',
    },
    '& .caption': {
      fontSize: '0.8rem',
      fontWeight: '500',
    },
    '& .strong': {
      fontWeight: '700',
    },
    '& .gutterBottom': {
      marginBottom: '0.35em',
    },
    '& .paragraph': {
      marginBottom: '20px',
    },
    [theme.breakpointUp('xs')]: {
      '& h1, .h1': {
        fontSize: '2.5707rem',
      },
      '& h2, .h2': {
        fontSize: '1.8219rem',
      },
      '& h3, .h3': {
        fontSize: '1.3118rem',
      },
    },
    [theme.breakpointUp('sm')]: {
      '& h1, .h1': {
        fontSize: '2.7849rem',
      },
      '& h2, .h2': {
        fontSize: '2.0243rem',
      },
      '& h3, .h3': {
        fontSize: '1.4993rem',
      },
    },
    [theme.breakpointUp('md')]: {
      '& h1, .h1': {
        fontSize: '2.9991rem',
      },
      '& h2, .h2': {
        fontSize: '2.0243rem',
      },
      '& h3, .h3': {
        fontSize: '1.4993rem',
      },
    },
  },
}));

/**
 * Provides base theme properties to the app.
 */
export default function RootWrapper({ className, style, ...rest }) {

  const classes = useStyles();

  return (
    <div
      id="botech-editor-container"
      className={className ? `${classes.root} ${className}` : classes.root}
      style={style}
      {...rest}
    />
  );
}
